import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { Col, Row, Form } from 'antd';
import {
    InputRenderer,
    GeneralInformationPanel,
    BasicRenewalPanelRenderer,
    LayeredRenewalPanelRenderer,
    BusinessSearchModal
} from '../../components';
import { PanelSwitch } from '../../../custom_shared/components';
import { NodeProps } from '../../componentTypes';
import { InstanceUtils, StyleUtils, InputIds, ChildNodeIds, NodeIds } from '../../misc';
import { useNodeForm } from '../../hooks';
import { useElementSize } from '../../../custom_shared/hooks';
import { useNodeColumnWidth } from '../../context/ColumnWidthContext';
import { InputComponentRegistry } from '../../node_inputs';
import './GeneralInformationNodeRenderer.less';

const InputComponents = InputComponentRegistry[NodeIds.GeneralInformation];

const Panels = {
    contractRenewal: {
        key: ChildNodeIds.GeneralInformation.ContractRenewal,
        label: 'Contract Renewal'
    },
    contractStructure: {
        key: 'ContractStructure',
        label: 'Contract Structure'
    },
    period: {
        key: 'Period',
        label: 'Period'
    },
    coverageClassification: {
        key: 'CoverageClassification',
        label: 'Coverage Classification'
    },
    partners: {
        key: 'Partners',
        label: 'Partners'
    }
} as const;

const GeneralInformationNodeRenderer: React.FC<NodeProps> = ({ node }: NodeProps) => {
    const { form, formKey, onValuesChange } = useNodeForm(node);

    const { setNodeColumnWidth } = useNodeColumnWidth();

    const [eightSpanColumn, setEightSpanColumn] = React.useState<HTMLDivElement | null>(null);

    const setEightSpanColumnRef = React.useCallback((div: HTMLDivElement | null) => setEightSpanColumn(div), []);

    const { width: eightSpanColumnWidth } = useElementSize(eightSpanColumn);

    // Provide a context with eight span column width of the node
    React.useEffect(() => {
        if (eightSpanColumnWidth) {
            setNodeColumnWidth(NodeIds.GeneralInformation, {
                span: 8,
                width: eightSpanColumnWidth
            });
        }
    }, [eightSpanColumnWidth, setNodeColumnWidth]);

    const containerRef = React.useRef<HTMLDivElement>(null);

    const panelRefs = React.useRef<Map<string, React.RefObject<HTMLDivElement>>>(new Map());

    const addRef = React.useCallback((panelKey: string, panelRef: React.RefObject<HTMLDivElement>) => {
        panelRefs.current.set(panelKey, panelRef);
    }, []);

    const getInputById = React.useCallback((id: string) => node.inputs.find(input => input.id === id), [node.inputs]);

    const inputs = React.useMemo(
        () => ({
            titleInput: getInputById(InputIds.GeneralInformation.Title),
            createProgramInput: getInputById(InputIds.GeneralInformation.CreateProgram),
            mainClassInput: getInputById(InputIds.GeneralInformation.MainClass),
            classInput: getInputById(InputIds.GeneralInformation.Class),
            subClassInput: getInputById(InputIds.GeneralInformation.SubClass),
            businessTypeInput: getInputById(InputIds.GeneralInformation.BusinessType),
            typeOfParticipationInput: getInputById(InputIds.GeneralInformation.TypeOfParticipation),
            underwritingYearInput: getInputById(InputIds.GeneralInformation.UnderwritingYear),
            periodStartInput: getInputById(InputIds.GeneralInformation.PeriodStart),
            periodEndInput: getInputById(InputIds.GeneralInformation.PeriodEnd),
            countryGroupsInput: getInputById(InputIds.GeneralInformation.CountryGroups),
            countriesInput: getInputById(InputIds.GeneralInformation.Countries),
            lifecycleInput: getInputById(InputIds.GeneralInformation.Lifecycle),
            currencyInput: getInputById(InputIds.GeneralInformation.Currency),
            insuredInput: getInputById(InputIds.GeneralInformation.Insured),
            reinsurerInput: getInputById(InputIds.GeneralInformation.Reinsurer),
            reportUnitsInput: getInputById(InputIds.GeneralInformation.ReportUnits),
            cedentInput: getInputById(InputIds.GeneralInformation.Cedent),
            brokerInput: getInputById(InputIds.GeneralInformation.Broker),
            businessIdInput: getInputById(InputIds.GeneralInformation.BusinessId),
            expiringPeriodStartInput: getInputById(InputIds.GeneralInformation.ExpiringPeriodStart)
        }),
        [getInputById]
    );

    const hasClassification = React.useMemo(
        () => !!(inputs.mainClassInput || inputs.classInput || inputs.subClassInput),
        [inputs.classInput, inputs.mainClassInput, inputs.subClassInput]
    );

    const hasCountries = React.useMemo(
        () => !!(inputs.countriesInput || inputs.countryGroupsInput),
        [inputs.countriesInput, inputs.countryGroupsInput]
    );

    const renderCoverageClassification = React.useMemo(
        () => hasClassification || hasCountries,
        [hasClassification, hasCountries]
    );

    const contractRenewalNode = React.useMemo(
        () => node.childNodes.find(t => t.id === ChildNodeIds.GeneralInformation.ContractRenewal),
        [node.childNodes]
    );

    const panelSwitchOptions = React.useMemo(
        () =>
            Object.values(Panels)
                .filter(panel => {
                    switch (panel.key) {
                        case 'CoverageClassification':
                            return renderCoverageClassification;
                        default:
                            return true;
                    }
                })
                .map(panel => ({
                    id: panel.key,
                    label: panel.label
                })),
        [renderCoverageClassification]
    );

    if (!InstanceUtils.isBasicNode(node)) {
        return null;
    }

    return (
        <>
            <BusinessSearchModal />

            <PanelSwitch options={panelSwitchOptions} panelsRefs={panelRefs} containerRef={containerRef} />

            <div ref={containerRef} className="general-information-node-renderer custom-scroll">
                <Form id={node.id} key={formKey} form={form} onValuesChange={onValuesChange}>
                    <div className="panels-container">
                        {contractRenewalNode && (
                            <LayeredRenewalPanelRenderer
                                node={contractRenewalNode}
                                panelName={Panels.contractRenewal.label}
                                panelKey={Panels.contractRenewal.key}
                                addRef={addRef}
                            />
                        )}

                        {inputs.businessIdInput && inputs.expiringPeriodStartInput && (
                            <BasicRenewalPanelRenderer
                                businessIdInput={inputs.businessIdInput}
                                expiringPeriodStartInput={inputs.expiringPeriodStartInput}
                                panelName={Panels.contractRenewal.label}
                                panelKey={Panels.contractRenewal.key}
                                addRef={addRef}
                            />
                        )}

                        <GeneralInformationPanel
                            panelKey={Panels.contractStructure.key}
                            panelName={Panels.contractStructure.label}
                            addRef={addRef}
                        >
                            <Row gutter={10}>
                                <Col style={StyleUtils.getNodeColumnStyle(16)}>
                                    <InputRenderer
                                        input={inputs.titleInput}
                                        component={InputComponents[InputIds.GeneralInformation.Title]}
                                    />
                                </Col>

                                <Col style={StyleUtils.getNodeColumnStyle(8)}>
                                    <InputRenderer
                                        input={inputs.createProgramInput}
                                        component={InputComponents[InputIds.GeneralInformation.CreateProgram]}
                                    />
                                </Col>
                            </Row>

                            <Row gutter={10}>
                                <Col ref={setEightSpanColumnRef} style={StyleUtils.getNodeColumnStyle(8)}>
                                    <InputRenderer
                                        input={inputs.businessTypeInput}
                                        component={InputComponents[InputIds.GeneralInformation.BusinessType]}
                                    />
                                </Col>

                                <Col style={StyleUtils.getNodeColumnStyle(8)}>
                                    <InputRenderer
                                        input={inputs.typeOfParticipationInput}
                                        component={InputComponents[InputIds.GeneralInformation.TypeOfParticipation]}
                                    />
                                </Col>

                                <Col style={StyleUtils.getNodeColumnStyle(8)}>
                                    <InputRenderer
                                        input={inputs.lifecycleInput}
                                        component={InputComponents[InputIds.GeneralInformation.Lifecycle]}
                                    />
                                </Col>
                            </Row>

                            <Row gutter={10}>
                                <Col style={StyleUtils.getNodeColumnStyle(8)}>
                                    <InputRenderer
                                        input={inputs.currencyInput}
                                        component={InputComponents[InputIds.GeneralInformation.Currency]}
                                    />
                                </Col>

                                <Col style={StyleUtils.getNodeColumnStyle(8)}>
                                    <InputRenderer
                                        input={inputs.reportUnitsInput}
                                        component={InputComponents[InputIds.GeneralInformation.ReportUnits]}
                                    />
                                </Col>
                            </Row>
                        </GeneralInformationPanel>

                        <GeneralInformationPanel
                            panelKey={Panels.period.key}
                            panelName={Panels.period.label}
                            addRef={addRef}
                        >
                            <Row gutter={10}>
                                <Col style={StyleUtils.getNodeColumnStyle(8)}>
                                    <InputRenderer
                                        input={inputs.underwritingYearInput}
                                        component={InputComponents[InputIds.GeneralInformation.UnderwritingYear]}
                                    />
                                </Col>

                                <Col style={StyleUtils.getNodeColumnStyle(8)}>
                                    <InputRenderer
                                        input={inputs.periodStartInput}
                                        component={InputComponents[InputIds.GeneralInformation.PeriodStart]}
                                    />
                                </Col>

                                <Col style={StyleUtils.getNodeColumnStyle(8)}>
                                    <InputRenderer
                                        input={inputs.periodEndInput}
                                        component={InputComponents[InputIds.GeneralInformation.PeriodEnd]}
                                    />
                                </Col>
                            </Row>
                        </GeneralInformationPanel>

                        {renderCoverageClassification && (
                            <GeneralInformationPanel
                                panelKey={Panels.coverageClassification.key}
                                panelName={Panels.coverageClassification.label}
                                addRef={addRef}
                            >
                                {hasClassification && (
                                    <>
                                        <Row gutter={10}>
                                            <Col style={StyleUtils.getNodeColumnStyle(24)}>
                                                <InputRenderer
                                                    input={inputs.mainClassInput}
                                                    component={InputComponents[InputIds.GeneralInformation.MainClass]}
                                                />
                                            </Col>
                                        </Row>

                                        <Row gutter={10}>
                                            <Col style={StyleUtils.getNodeColumnStyle(24)}>
                                                <InputRenderer
                                                    input={inputs.classInput}
                                                    component={InputComponents[InputIds.GeneralInformation.Class]}
                                                />
                                            </Col>
                                        </Row>

                                        <Row gutter={10}>
                                            <Col style={StyleUtils.getNodeColumnStyle(24)}>
                                                <InputRenderer
                                                    input={inputs.subClassInput}
                                                    component={InputComponents[InputIds.GeneralInformation.SubClass]}
                                                />
                                            </Col>
                                        </Row>
                                    </>
                                )}

                                {hasCountries && (
                                    <Row gutter={10}>
                                        <Col style={StyleUtils.getNodeColumnStyle(8)}>
                                            <InputRenderer
                                                input={inputs.countryGroupsInput}
                                                component={InputComponents[InputIds.GeneralInformation.CountryGroups]}
                                            />
                                        </Col>

                                        <Col style={StyleUtils.getNodeColumnStyle(16)}>
                                            <InputRenderer
                                                input={inputs.countriesInput}
                                                component={InputComponents[InputIds.GeneralInformation.Countries]}
                                            />
                                        </Col>
                                    </Row>
                                )}
                            </GeneralInformationPanel>
                        )}

                        <GeneralInformationPanel
                            panelKey={Panels.partners.key}
                            panelName={Panels.partners.label}
                            addRef={addRef}
                        >
                            <Row gutter={[10, 10]} wrap>
                                {inputs.cedentInput && (
                                    <Col style={StyleUtils.getNodeColumnStyle(8)}>
                                        <InputRenderer
                                            input={inputs.cedentInput}
                                            component={InputComponents[InputIds.GeneralInformation.Cedent]}
                                        />
                                    </Col>
                                )}

                                {inputs.brokerInput && (
                                    <Col style={StyleUtils.getNodeColumnStyle(8)}>
                                        <InputRenderer
                                            input={inputs.brokerInput}
                                            component={InputComponents[InputIds.GeneralInformation.Broker]}
                                        />
                                    </Col>
                                )}

                                {inputs.insuredInput && (
                                    <Col style={StyleUtils.getNodeColumnStyle(8)}>
                                        <InputRenderer
                                            input={inputs.insuredInput}
                                            component={InputComponents[InputIds.GeneralInformation.Insured]}
                                        />
                                    </Col>
                                )}

                                <Col style={StyleUtils.getNodeColumnStyle(8)}>
                                    <InputRenderer
                                        input={inputs.reinsurerInput}
                                        component={InputComponents[InputIds.GeneralInformation.Reinsurer]}
                                    />
                                </Col>
                            </Row>
                        </GeneralInformationPanel>
                    </div>
                </Form>
            </div>
        </>
    );
};

export default observer(GeneralInformationNodeRenderer);
